/**
 * Spinel · Tournament Director — admin console (Round 53).
 * Tell it what to set up in a sentence; see each tournament's launch kit and
 * what is waiting in Decisions; set which channels may act by themselves.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Trophy, Send, RefreshCw, Sparkles, Megaphone, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';

const BASE = '/api/admin/workforce/spinel';
const errText = (e, fallback) => e?.response?.data?.detail || fallback;
const STATUS = { draft: 'Draft', registration: 'Sign-up open', closed: 'Sign-up closed', checkin: 'Check-in', in_progress: 'Tonight' };
const KIND = { push: 'Push', dm: 'Message from GEMS', email: 'Email', social: 'Social posts' };

const SpinelPage = () => {
  const { api } = useAdmin();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [reply, setReply] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try { setData((await api.get(`${BASE}/overview`)).data); } catch (e) { toast.error('Could not load Spinel'); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); if (ok) toast.success(typeof ok === 'function' ? ok(r) : ok); await load(); return r; }
    catch (e) { toast.error(errText(e, 'That did not work')); return null; }
    finally { setBusy(''); }
  };
  const ask = async () => {
    if (text.trim().length < 8) return;
    setReply(null);
    const r = await call('ask', () => api.post(`${BASE}/ask`, { text }));
    if (r) {
      setReply(r.data);
      if (!r.data.questions) { setText(''); toast.success('Filed in Decisions — approve it there'); }
    }
  };
  const setMode = (key, mode) => call(`mode:${key}`, () => api.post(`${BASE}/config`, { [`mode_${key}`]: mode }), mode === 'auto' ? 'On auto — it acts without asking' : 'Back to asking first');

  if (!data) return <div className="p-6 text-sm text-gray-500">Loading Spinel…</div>;
  const cfg = data.config;

  return (
    <div className="p-4 sm:p-6 space-y-5 max-w-6xl" data-testid="spinel-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-600" /> Spinel · Tournament Director</h2>
          <p className="text-sm text-gray-500">{data.waiting} waiting in Decisions · everything asks first unless a channel is on auto</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => call('watch', () => api.post(`${BASE}/watch-now`), (r) => `Checked: ${Object.entries(r.data).filter(([, v]) => v).map(([k, v]) => `${k} ${v}`).join(', ') || 'nothing to file'}`)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm"><Sparkles className="w-4 h-4" /> Check now</button>
          <button onClick={load} className="p-2 rounded-lg border text-gray-600" title="Refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {/* tell Spinel */}
      <div className="bg-white border rounded-xl p-4">
        <p className="text-sm font-semibold text-gray-900">Tell Spinel what to set up</p>
        <p className="text-xs text-gray-500 mt-0.5">Say the day, the time, the size and the entry. It fills in the rest from the rules, tells you what it assumed, and files “Create …” in Decisions. Nothing is created until you approve it there.</p>
        <div className="mt-3 flex gap-2">
          <input className="flex-1 border rounded-lg px-3 py-2 text-sm" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && ask()}
            placeholder={data.examples?.[0]} data-testid="spinel-ask" />
          <button onClick={ask} disabled={busy === 'ask' || text.trim().length < 8} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold disabled:opacity-50"><Send className="w-4 h-4" /> Ask</button>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">{(data.examples || []).map((ex) => <button key={ex} onClick={() => setText(ex)} className="text-xs px-2 py-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200">{ex}</button>)}</div>
        {reply && reply.questions && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
            <p className="font-semibold flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Spinel needs one more thing</p>
            <ul className="list-disc ml-5 mt-1">{reply.questions.map((q) => <li key={q}>{q}</li>)}</ul>
            <p className="mt-2 text-xs">Understood so far: {reply.understood}{reply.assumed?.length ? ` · assumed: ${reply.assumed.join('; ')}` : ''}</p>
          </div>
        )}
        {reply && !reply.questions && (
          <div className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
            <p className="font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> {reply.title}</p>
            <p className="mt-1">{reply.summary}</p>
            <button onClick={() => navigate('/decisions')} className="mt-2 text-xs px-2 py-1 rounded-md bg-emerald-600 text-white font-semibold">Open Decisions</button>
          </div>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {/* tournaments and their kits */}
        <div className="lg:col-span-2 bg-white border rounded-xl p-4">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2"><Megaphone className="w-4 h-4" /> Tournaments and their launch kits</p>
          <p className="text-xs text-gray-500 mt-0.5">The kit = push + notification, a message from GEMS, the email blast, social posts with the flyer. Filed when a tournament is announced and again when sign-up opens — or now, with the button.</p>
          {data.tournaments.length === 0 && <p className="mt-3 text-sm text-gray-500">No tournament yet. Tell Spinel to set one up, or create one in Admin → Tournaments.</p>}
          <div className="mt-3 divide-y">
            {data.tournaments.map((t) => {
              const stage = t.status === 'draft' && t.announced ? 'announce' : t.status === 'registration' ? 'open' : null;
              return (
                <div key={t.id} className="py-3 flex flex-wrap items-start gap-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <button onClick={() => navigate('/tournaments')} className="font-semibold text-gray-900 hover:underline">{t.name}</button>
                    <p className="text-xs text-gray-500">{[STATUS[t.status] || t.status, t.announced && t.status === 'draft' ? 'announced' : null, t.starts_label, `${t.teams} of ${t.max_teams} teams`, t.below_minimum ? 'below the minimum' : null].filter(Boolean).join(' · ')}</p>
                    {Object.entries(t.kit || {}).map(([k, v]) => (
                      <p key={k} className="text-xs text-gray-600 mt-1"><span className="font-medium">{k === 'announce' ? 'Announce kit' : 'Sign-up kit'}:</span> {v.items.map((i) => `${KIND[i.kind] || i.kind} ${i.status || 'proposed'}`).join(' · ')}</p>
                    ))}
                    {t.bracket && !t.approved && <p className="text-xs text-amber-700 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Draft bracket waiting — approve it in Decisions or Admin → Tournaments</p>}
                  </div>
                  {stage && !(t.kit || {})[stage] && (
                    <button onClick={() => call(`kit:${t.id}`, () => api.post(`${BASE}/tournaments/${t.id}/kit`, { stage }), (r) => `Kit filed: ${r.data.items.map((i) => `${KIND[i.kind] || i.kind} ${i.status || 'proposed'}`).join(', ')}`)}
                      disabled={busy === `kit:${t.id}`} className="text-xs px-3 py-1.5 rounded-lg bg-amber-600 text-white font-semibold" data-testid={`spinel-kit-${t.id}`}>File the {stage === 'announce' ? 'announce' : 'sign-up'} kit</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* channels */}
        <div className="bg-white border rounded-xl p-4">
          <p className="text-sm font-semibold text-gray-900">What it may do by itself</p>
          <p className="text-xs text-gray-500 mt-0.5">Propose = it asks in Decisions first. Auto = it acts and tells you. Rewards always ask.</p>
          <div className="mt-3 space-y-2">
            {data.channels.map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-gray-800">{c.label}</span>
                <select value={c.mode} onChange={(e) => (e.target.value === 'propose' || window.confirm(`Let Spinel handle “${c.label}” without asking?`)) && setMode(c.key, e.target.value)} className="border rounded-md px-2 py-1 text-xs" data-testid={`spinel-mode-${c.key}`}>
                  <option value="propose">asks first</option><option value="auto">auto</option>
                </select>
              </div>
            ))}
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!cfg.announce_on_create} onChange={(e) => call('cfg', () => api.post(`${BASE}/config`, { announce_on_create: e.target.checked }), 'Saved')} /> Announce with “Notify me” when a tournament is created from an instruction</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!cfg.kit_on_announce} onChange={(e) => call('cfg', () => api.post(`${BASE}/config`, { kit_on_announce: e.target.checked }), 'Saved')} /> File the kit when a tournament is announced</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!cfg.kit_on_open} onChange={(e) => call('cfg', () => api.post(`${BASE}/config`, { kit_on_open: e.target.checked }), 'Saved')} /> File the kit when sign-up opens</label>
            <label className="flex items-center gap-2">Check-in reminder after <input type="number" min={3} max={60} value={cfg.reminder_minutes} onChange={(e) => call('cfg', () => api.post(`${BASE}/config`, { reminder_minutes: e.target.value }))} className="w-16 border rounded-md px-2 py-1 text-xs" /> minutes</label>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <details className="bg-white border rounded-xl p-4" open>
          <summary className="font-semibold text-gray-900 cursor-pointer">What it sent</summary>
          <div className="mt-3 space-y-1 text-xs text-gray-600">
            {data.actions.length === 0 && <p>Nothing yet.</p>}
            {data.actions.map((a) => <p key={a.id}><span className="text-gray-400">{new Date(a.created_at).toLocaleString()}</span> — {KIND[a.kind] || a.kind} · {a.tournament_name} · {a.stage} · <span className={a.status === 'sent' ? 'text-emerald-700' : a.status === 'failed' ? 'text-red-600' : ''}>{a.status}{a.result?.note ? ` (${a.result.note})` : a.error ? ` (${a.error})` : ''}</span></p>)}
          </div>
        </details>
        <details className="bg-white border rounded-xl p-4">
          <summary className="font-semibold text-gray-900 cursor-pointer">Activity</summary>
          <div className="mt-3 space-y-1 text-xs text-gray-600">{data.logs.map((l, i) => <p key={i}><span className="text-gray-400">{new Date(l.timestamp).toLocaleString()}</span> — {l.message}</p>)}</div>
        </details>
      </div>
    </div>
  );
};

export default SpinelPage;
